import {
  type ClaimResult,
  useAroundNavBoundary,
} from "@/components/navigation/useAroundNavBoundary.ts";
import { getCurrentTimezone } from "@/lib/user.ts";
import { useCallback, type ReactNode } from "react";
import { Router } from "wouter";
import { parseCalendarUrl } from "./calendar-url.ts";
import { applyCalendarFocus, hasCalendarFocus } from "./calendarFocus.ts";

/**
 * Navigation boundary for a mounted calendar: turns a `/calendar?date=&view=&r=`
 * link into a move of the calendar already on screen instead of a page
 * navigation.
 *
 * Unlike {@link CanvasNavBoundary} this needs no action context — the view/date
 * and the range selection are both shared stores, so {@link applyCalendarFocus}
 * writes them directly and whichever surface is mounted follows. That makes the
 * mount point the whole signal: this only wraps regions that have a calendar on
 * screen, and anywhere else the link is left alone and loads the real
 * `/calendar` page (which seeds itself from the same params).
 *
 * `active` handles the surfaces that can hide their calendar without unmounting
 * this boundary — a thread whose canvas is collapsed, the mobile Chats tab.
 * Focusing a calendar the user can't see reads as a dead link, so those delegate
 * outward to the real page instead.
 *
 * `onFocus` is for page-level reveals that the shared stores can't express (the
 * mobile calendar page switching off its Chats tab).
 */
export const CalendarFocusNavBoundary = ({
  active,
  onFocus,
  children,
}: {
  active: boolean;
  onFocus?: () => void;
  children: ReactNode;
}) => {
  const claim = useCallback(
    (to: string): ClaimResult => {
      if (!active) return "delegate";
      const parts = parseCalendarUrl(to);
      // Not a calendar link, or a bare `/calendar` with nothing to focus.
      if (!parts || !hasCalendarFocus(parts)) return "delegate";
      applyCalendarFocus(parts, getCurrentTimezone());
      onFocus?.();
      return "claimed";
    },
    [active, onFocus],
  );

  const aroundNav = useAroundNavBoundary(claim);
  return <Router aroundNav={aroundNav}>{children}</Router>;
};
